<script setup lang="ts">
import { onClickOutside } from '@vueuse/core';
import type FilterOptions from '@/components/base/search/FilterOverlay.vue';

interface IProps {
  modelValue: string;
  ableToOverlay?: boolean;
  scrollWhenFocus?: boolean;
  isLoading?: boolean;
  id?: string;
  name?: string;
  placeholder?: string;
}

const props = withDefaults(defineProps<IProps>(), {
  ableToOverlay: false,
  scrollWhenFocus: false,
  isLoading: false,
  id: '',
  name: '',
  placeholder: '',
});

type Emits = {
  'update:modelValue': [string];
  overlayStatus: [boolean];
  setSportsFilterActive: [boolean];
  clearSearch: [];
  search: [string];
};

const emit = defineEmits<Emits>();

const { t } = useTexts();
const filterOptionsRef = ref<typeof FilterOptions | null>(null);
const searchText = ref<string>('');
const isOverlayActive = ref<boolean>(false);
const showFilterOptions = ref<boolean>(false);
const debounceTimer = ref<NodeJS.Timeout | null>(null);

const { isCactusSportsbookEnabled } = useFeatureFlagsEnv();
const { isSportsSearchEnabled } = useFeatureFlagsNew();

const showFilterSection = computed(() => {
  return isCactusSportsbookEnabled.value && isSportsSearchEnabled.value;
});

const filterOptions = ref<any[]>([
  { value: 'casino', label: t('home.casino'), icon: 'poker' },
  { value: 'sports', label: t('home.sports'), icon: 'futbol' },
]);

const selectedFilter = ref<any>(filterOptions.value[0]);
const searchInputRef = ref<HTMLInputElement | null>(null);
const filterBtnRef = ref<HTMLButtonElement | null>(null);

// const { startAnimation, stopAnimation } = useTypewriterAnimation();
// const isUserTyping = ref(false);

const placeholderText = computed(() => {
  if (props.placeholder) {
    return props.placeholder;
  }

  return selectedFilter.value.value === 'sports'
    ? t('components.search_bar.placeholder_sports')
    : t('components.search_bar.placeholder');
});

onClickOutside(filterOptionsRef.value?.$el ?? filterOptionsRef, (event) => {
  showFilterOptions.value = false;
  event.stopPropagation();
});

const { isLayoutMobileOrTablet, headerHeight, topBarHeight } = useLayout();

watch(
  () => props.modelValue,
  () => setSearchTextToPropsValue(),
  { deep: true },
);

watch(
  () => isOverlayActive.value,
  () => emitOverlayStatusWhenChange(),
);

watch(
  () => selectedFilter.value,
  () => emitFilter(),
);

const emitOverlayStatusWhenChange = (): void => {
  emit('overlayStatus', isOverlayActive.value);
};

const emitFilter = () => {
  emit('setSportsFilterActive', selectedFilter.value.value === 'sports');
};

const setSearchTextToPropsValue = (): void => {
  searchText.value = props.modelValue;
  // Só fecha o overlay se o texto estiver vazio E o input não estiver com foco
  if (
    !searchText.value.length &&
    document.activeElement !== searchInputRef.value
  ) {
    return deactivateOverlay();
  }
  if (searchText.value.length) {
    activateOverlay();
  }
};

const updateModelValue = (): void => {
  // Limpa o timeout anterior se existir
  if (debounceTimer.value) {
    clearTimeout(debounceTimer.value);
  }

  // Se tem apenas 1 letra, aplica debounce de 200ms
  if (searchText.value.length === 1) {
    debounceTimer.value = setTimeout(() => {
      emit('update:modelValue', searchText.value);
    }, 200);
  } else {
    // Para 0 ou 2+ letras, emite imediatamente
    emit('update:modelValue', searchText.value);
  }
};

const activateOverlay = (): void => {
  if (!props.ableToOverlay) return;
  // isUserTyping.value = true;
  isOverlayActive.value = true;
  document.body.style.overflow = 'hidden';
  if (props.scrollWhenFocus) scrollTop();
};

function onInputFocusSearch() {
  activateOverlay();
  // Efetua busca igual ao clique na lupa se houver texto
  if (searchText.value && searchText.value.length > 0) {
    emit('update:modelValue', searchText.value);
    emit('search', searchText.value);
  }
}

const searchInputContainerRef = ref<HTMLDivElement>();

const scrollTop = (): void => {
  const MARGIN_ADD = 20;

  if (!searchInputContainerRef.value) return;

  const rect = searchInputContainerRef.value.getBoundingClientRect();
  const elementTop = (rect?.top || 200) + document.body.scrollTop;
  const scrollTopValue =
    elementTop - headerHeight.value - topBarHeight.value - MARGIN_ADD;

  scrollToTop(scrollTopValue);
};

const sleep = (miliseconds: number): Promise<boolean> => {
  return new Promise((resolve) => setTimeout(() => resolve(true), miliseconds));
};

const removeOverflow = async (): Promise<void> => {
  await sleep(601);
  document.body.style.removeProperty('overflow');
};

const deactivateOverlay = (): void => {
  if (!props.ableToOverlay) return;
  isOverlayActive.value = false;
  showFilterOptions.value = false;
  searchInputRef.value?.blur();
  document.body.style.overflow = 'auto';
  removeOverflow();
};

function onInputBlur() {
  // deactivateOverlay();
}

function onClickFilterBtn() {
  showFilterOptions.value = !showFilterOptions.value;
  if (searchText.value) {
    searchInputRef.value?.focus();
  }
}

function onSelectFilter(opt: any) {
  selectedFilter.value = opt;
  if (searchText.value) searchInputRef.value?.focus();
}

const clearSearch = (): void => {
  searchText.value = '';
  // isUserTyping.value = false;
  emit('clearSearch');
  emit('update:modelValue', '');
};

// onMounted(() => {
//   // Iniciar a animação após o componente ser montado
//   setTimeout(() => {
//     startAnimation(
//       searchInputRef,
//       (text: string) => {
//         if (!isUserTyping.value) {
//           searchText.value = text;
//         }
//       },
//       () => !isUserTyping.value && !isOverlayActive.value,
//     );
//   }, 2000);
// });

onUnmounted(() => {
  // Limpa o timeout ao desmontar o componente
  if (debounceTimer.value) {
    clearTimeout(debounceTimer.value);
  }
  // stopAnimation();
});

function onClickSearchIcon() {
  // Busca sempre que houver texto ao clicar na lupa
  activateOverlay();
  emit('update:modelValue', searchText.value);
  emit('search', searchText.value);
}
</script>

<template>
  <div>
    <div
      v-if="isOverlayActive"
      :class="$style.searchOverlay"
      @click="deactivateOverlay"
    />
    <div
      ref="searchInputContainerRef"
      :class="$style.baseSearchInput"
    >
      <input
        :id="props.id || 'search-input'"
        ref="searchInputRef"
        v-model="searchText"
        :class="$style.searchInput"
        :name="props.name || 'search'"
        :placeholder="placeholderText"
        style="color: #111"
        type="text"
        @blur="onInputBlur"
        @focus="onInputFocusSearch"
        @input="updateModelValue()"
        @keyup.enter="hideMobileKeyboard($event.target)"
      />
      <section
        v-if="showFilterSection"
        :class="$style.filterSection"
      >
        <button
          ref="filterBtnRef"
          :class="$style.filterButton"
          @click="onClickFilterBtn"
        >
          <NuxtIcon
            :class="$style.filterIcon"
            :name="selectedFilter?.icon"
          />
          <span v-if="!isOverlayActive || !isLayoutMobileOrTablet">
            {{ selectedFilter?.label }}
          </span>
          <NuxtIcon
            :class="$style.filterIconAngle"
            name="arrows/angle_down"
          />
          <BaseSearchFilterOverlay
            v-if="showFilterOptions"
            ref="filterOptionsRef"
            :filter-options="filterOptions"
            @select-filter="onSelectFilter"
          />
        </button>
      </section>
      <NuxtIcon
        v-if="searchText.length > 0"
        class="text-bg-primary/70 cursor-pointer text-sm rounded w-5 p-0 m-0 ml-2"
        name="close"
        @click.prevent="clearSearch"
      />
      <NuxtIcon
        v-if="props.isLoading"
        class="text-bg-primary animate-spin"
        filled
        name="loading"
      />
      <NuxtIcon
        v-else
        class="text-bg-primary cursor-pointer text-[18px]"
        filled
        name="apostatudo/search-lens"
        @click.stop="onClickSearchIcon"
      />
    </div>
  </div>
</template>

<style module lang="scss">
.searchOverlay {
  @apply z-[60] fixed top-0 left-0 w-full h-full bg-[#0d1c2b]/15 backdrop-blur-[5px];
}
.baseSearchInput {
  @apply px-2 flex h-[36px] items-center justify-between bg-white md:px-3 lg:px-3 relative z-[80];
  border-radius: 14px;
  border: 2px solid var(--colors-base-default-400, #a1a1aa);
  background: var(--colors-base-default-200, #e4e4e7);
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

  .searchInput {
    @apply w-full h-[36px] ml-0 bg-transparent font-extrabold text-black/80 text-[12px] px-1 lg:px-2;
    &::placeholder {
      @apply text-bg-primary/80;
      font-size: 12px;
      font-style: normal;
      font-weight: 800;
    }
    @apply autofill:shadow-white !important;
    /* Corrige cor do texto no autofill/histórico */
    &:-webkit-autofill {
      -webkit-text-fill-color: #111 !important;
      transition: background-color 9999s ease-in-out 0s;
    }
    &:-webkit-autofill:focus {
      -webkit-text-fill-color: #111 !important;
    }
    &:-webkit-autofill:hover {
      -webkit-text-fill-color: #111 !important;
    }
    &:-webkit-autofill:active {
      -webkit-text-fill-color: #111 !important;
    }
  }

  .filterSection {
    @apply mr-0 flex gap-1 text-xs text-texts/60 items-center;

    .filterButton {
      @apply relative py-1 lg:py-1.5 px-2 flex items-center justify-between gap-2 lg:gap-3 rounded-md text-xs bg-bg-primary text-texts/80 hover:opacity-100;

      .filterIcon {
        @apply text-primary text-2xs sm:text-xs lg:text-sm opacity-100;
      }

      .filterIconAngle {
        @apply text-texts/60 text-2xs -ml-1 -mb-px;
      }
    }
  }
}
</style>
